import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Trash2, Clock, X, Save, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: 'monday', label: 'Poniedziałek' },
  { key: 'tuesday', label: 'Wtorek' },
  { key: 'wednesday', label: 'Środa' },
  { key: 'thursday', label: 'Czwartek' },
  { key: 'friday', label: 'Piątek' },
  { key: 'saturday', label: 'Sobota' },
  { key: 'sunday', label: 'Niedziela' }
];

const AdminHours = () => {
  const { getAuthHeader } = useAdminAuth();
  const [hours, setHours] = useState({ active: null, all: [] });
  const [loading, setLoading] = useState(true);
  const [editingHours, setEditingHours] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // System settings for booking
  const [systemSettings, setSystemSettings] = useState({
    scheduleDaysAhead: 14,
    allowSameDayBooking: false,
    sameDayBookingCutoffHours: 2,
    passActivationDeadlineDays: 60
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const emptyHours = {
    name: '',
    schedule: DAYS.reduce((acc, day) => {
      acc[day.key] = { open: '12:00', close: '22:00', closed: false };
      return acc;
    }, {}),
    validFrom: '',
    validTo: '',
    isActive: true
  };

  useEffect(() => {
    fetchHours();
    fetchSystemSettings();
  }, []);

  const fetchHours = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/hours?all=true`, {
        headers: getAuthHeader()
      });
      setHours(response.data);
    } catch (error) {
      console.error('Failed to fetch hours:', error);
      toast.error('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/settings`, {
        headers: getAuthHeader()
      });
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };
  
  const saveSystemSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/api/admin/settings`, systemSettings, {
        headers: getAuthHeader()
      });
      toast.success('Ustawienia zapisane');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Nie udało się zapisać ustawień');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/api/admin/hours`, editingHours, {
          headers: getAuthHeader()
        });
        toast.success('Harmonogram utworzony');
      } else {
        await axios.put(`${API}/api/admin/hours/${editingHours.hours_id}`, editingHours, {
          headers: getAuthHeader()
        });
        toast.success('Harmonogram zaktualizowany');
      }
      setEditingHours(null);
      setIsCreating(false);
      fetchHours();
    } catch (error) {
      toast.error('Nie udało się zapisać');
    }
  };

  const handleDelete = async (hoursId) => {
    if (!confirm('Czy na pewno chcesz usunąć ten harmonogram?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/hours/${hoursId}`, {
        headers: getAuthHeader()
      });
      toast.success('Harmonogram usunięty');
      fetchHours();
    } catch (error) {
      toast.error('Nie udało się usunąć');
    }
  };

  const updateDaySchedule = (day, field, value) => {
    setEditingHours({
      ...editingHours,
      schedule: {
        ...editingHours.schedule,
        [day]: {
          ...editingHours.schedule[day],
          [field]: value
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* System Settings for Booking */}
      <Card className="bg-purple-500/10 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-400" />
            Ustawienia harmonogramu rezerwacji
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Days ahead */}
            <div className="space-y-2">
              <Label className="text-gray-300">Liczba dni w przód</Label>
              <p className="text-xs text-gray-500">Ile dni naprzód pokazywać w harmonogramie rezerwacji</p>
              <Input
                type="number"
                min="1"
                max="60"
                value={systemSettings.scheduleDaysAhead}
                onChange={(e) => setSystemSettings(prev => ({ 
                  ...prev, 
                  scheduleDaysAhead: parseInt(e.target.value) || 14 
                }))}
                className="bg-white/5 border-white/10 text-white w-24"
              />
            </div>
            
            {/* Same day booking */}
            <div className="space-y-2">
              <Label className="text-gray-300">Rezerwacja na dziś</Label>
              <p className="text-xs text-gray-500">Czy pozwalać na rezerwację na bieżący dzień</p>
              <div className="flex items-center gap-3">
                <Switch
                  checked={systemSettings.allowSameDayBooking}
                  onCheckedChange={(checked) => setSystemSettings(prev => ({ 
                    ...prev, 
                    allowSameDayBooking: checked 
                  }))}
                />
                <span className="text-sm text-gray-400">
                  {systemSettings.allowSameDayBooking ? 'Włączone' : 'Wyłączone'}
                </span>
              </div>
            </div>
            
            {/* Cutoff hours */}
            {systemSettings.allowSameDayBooking && (
              <div className="space-y-2">
                <Label className="text-gray-300">Godziny przed zajęciami</Label>
                <p className="text-xs text-gray-500">Minimum godzin przed zajęciami na rezerwację</p>
                <Input
                  type="number"
                  min="0"
                  max="12"
                  value={systemSettings.sameDayBookingCutoffHours}
                  onChange={(e) => setSystemSettings(prev => ({ 
                    ...prev, 
                    sameDayBookingCutoffHours: parseInt(e.target.value) || 2 
                  }))}
                  className="bg-white/5 border-white/10 text-white w-24"
                />
              </div>
            )}
            
            {/* Pass activation deadline */}
            <div className="space-y-2">
              <Label className="text-gray-300">Dni na aktywację karnetu</Label>
              <p className="text-xs text-gray-500">Ile dni klient ma na aktywację kupionego karnetu</p>
              <Input
                type="number"
                min="1"
                max="365"
                value={systemSettings.passActivationDeadlineDays || 60}
                onChange={(e) => setSystemSettings(prev => ({ 
                  ...prev, 
                  passActivationDeadlineDays: parseInt(e.target.value) || 60 
                }))}
                className="bg-white/5 border-white/10 text-white w-24"
              />
            </div>
          </div>
          
          <Button
            onClick={saveSystemSettings}
            disabled={savingSettings}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {savingSettings ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                Zapisywanie...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Zapisz ustawienia
              </>
            )}
          </Button>
        </CardContent>
      </Card>
      
      {/* Opening Hours Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Godziny Otwarcia</h1>
          <p className="text-gray-400">Zarządzaj harmonogramami otwarcia</p>
        </div>
        <Button
          onClick={() => { setEditingHours(emptyHours); setIsCreating(true); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nowy Harmonogram
        </Button>
      </div>

      {/* Active Schedule */}
      {hours.active && (
        <Card className="bg-green-500/10 border-green-500/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-green-400" />
                Aktywny harmonogram: {hours.active.name || 'Brak nazwy'}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setEditingHours(hours.active); setIsCreating(false); }}
                className="text-green-400 hover:text-white"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edytuj
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {DAYS.map(day => {
                const schedule = hours.active.schedule?.[day.key];
                return (
                  <div key={day.key} className="p-3 bg-white/5 rounded-lg text-center">
                    <p className="text-gray-400 text-xs mb-1">{day.label}</p>
                    {schedule?.closed ? (
                      <p className="text-red-400 font-medium">Zamknięte</p>
                    ) : (
                      <p className="text-white font-medium">
                        {schedule?.open || '--'} - {schedule?.close || '--'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Schedules */}
      {hours.all && hours.all.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-white">Wszystkie harmonogramy</h2>
          {hours.all.map((schedule) => (
            <Card key={schedule.hours_id} className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className={`w-5 h-5 ${schedule.isActive ? 'text-green-400' : 'text-gray-500'}`} />
                    <div>
                      <h3 className="text-white font-medium">{schedule.name || 'Brak nazwy'}</h3>
                      <p className="text-gray-500 text-xs">
                        {schedule.validFrom && `Od: ${new Date(schedule.validFrom).toLocaleDateString('pl-PL')}`}
                        {schedule.validTo && ` Do: ${new Date(schedule.validTo).toLocaleDateString('pl-PL')}`}
                        {!schedule.validFrom && !schedule.validTo && 'Bez limitu czasowego'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      schedule.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'
                    }`}>
                      {schedule.isActive ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setEditingHours(schedule); setIsCreating(false); }}
                      className="text-gray-400 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(schedule.hours_id)}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(!hours.all || hours.all.length === 0) && !hours.active && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-8 text-center">
            <Clock className="w-12 h-12 mx-auto text-gray-500 mb-4" />
            <p className="text-gray-400">Brak harmonogramów</p>
            <Button
              onClick={() => { setEditingHours(emptyHours); setIsCreating(true); }}
              variant="outline"
              className="mt-4"
            >
              Utwórz pierwszy harmonogram
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      {editingHours && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10">
              <CardTitle className="text-white">
                {isCreating ? 'Nowy Harmonogram' : 'Edytuj Harmonogram'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingHours(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label className="text-gray-300">Nazwa harmonogramu</Label>
                <Input
                  value={editingHours.name || ''}
                  onChange={(e) => setEditingHours({ ...editingHours, name: e.target.value })}
                  placeholder="np. Harmonogram letni"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Schedule Grid */}
              <div className="space-y-3">
                <Label className="text-gray-300">Godziny otwarcia</Label>
                {DAYS.map(day => (
                  <div key={day.key} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <div className="w-28 text-white">{day.label}</div>
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={editingHours.schedule?.[day.key]?.open || '12:00'}
                        onChange={(e) => updateDaySchedule(day.key, 'open', e.target.value)}
                        disabled={editingHours.schedule?.[day.key]?.closed}
                        className="bg-white/5 border-white/10 text-white w-28"
                      />
                      <span className="text-gray-400">-</span>
                      <Input
                        type="time"
                        value={editingHours.schedule?.[day.key]?.close || '22:00'}
                        onChange={(e) => updateDaySchedule(day.key, 'close', e.target.value)}
                        disabled={editingHours.schedule?.[day.key]?.closed}
                        className="bg-white/5 border-white/10 text-white w-28"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editingHours.schedule?.[day.key]?.closed || false}
                        onCheckedChange={(checked) => updateDaySchedule(day.key, 'closed', checked)}
                      />
                      <span className="text-gray-400 text-sm">Zamknięte</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Obowiązuje od</Label>
                  <Input
                    type="date"
                    value={editingHours.validFrom ? new Date(editingHours.validFrom).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditingHours({ ...editingHours, validFrom: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Obowiązuje do</Label>
                  <Input
                    type="date"
                    value={editingHours.validTo ? new Date(editingHours.validTo).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditingHours({ ...editingHours, validTo: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingHours.isActive}
                  onCheckedChange={(checked) => setEditingHours({ ...editingHours, isActive: checked })}
                />
                <Label className="text-gray-300">Aktywny</Label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingHours(null); setIsCreating(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminHours;
